/**
 * @file NoteComposePage.tsx
 * @brief Write or edit a typed note on the web — a numbered list of points.
 *
 * @details
 * The web twin of mobile's `NoteComposeScreen`, and deliberately the same
 * editor rather than a text box: a prep note is a set of discrete points, and
 * agenda coverage checks them one at a time. A free-form textarea would only
 * push the job of splitting the text onto a model later — at cost, guessing at
 * boundaries the writer already knew.
 *
 * **Splitting on `\n` inside the change handler is the whole mechanism**, on
 * both surfaces. A `<textarea>` wraps long points and inserts a newline on
 * Return, so intercepting the key is unnecessary: the split covers Return and a
 * pasted multi-line block by the same path, and keeps the invariant that one
 * row is one line. Rows are keyed by id, not index — an index key makes React
 * reuse the wrong row's state when one is removed from the middle, which reads
 * as text jumping between lines.
 *
 * Nothing here is summarised, scored, or sent anywhere: a typed note is free
 * and runs no AI at all.
 *
 * **Looks like the phone (DES, 30-09-2026).** A large bold title field; the
 * points on the same numbered timeline as the detail page (the number on a
 * rail broken around it); a tall tap area under the list; and "Save note"
 * docked at the bottom as one full pill, not a pair of header buttons.
 * Saving opens the note, as mobile does.
 *
 * @dependencies
 * - features/notes/api: useNote, useCreateTypedNote, useUpdateNote
 *
 * @references
 * - FRONTEND/src/screens/Notes/NoteComposeScreen.tsx (the mobile twin)
 * - BACKEND/src/features/notes/routes.js (POST / and PATCH /:noteId)
 * - docs/product/voice-notes.md §6b
 *
 * @author DES
 * @date 21-08-2026
 * @updated 30-09-2026 — mobile look and feel; save opens the note
 * @updated 12-10-2026: sub-points, Tab indents a point under the one above,
 *   Shift+Tab brings it back (one level, as the phone)
 * @updated 12-10-2026: agendas. `?kind=agenda` writes an agenda ("Write an
 *   Agenda"); editing one says "Edit Agenda". Opened from a meeting's agenda
 *   picker (router state `agendaPicker`), saving goes back to Setup, where
 *   the new agenda is attached (agendaHandoff.ts).
 */

import { Skeleton } from "@/components/ui/Skeleton";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ListIndentDecrease, ListIndentIncrease } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/shared/theme/cn";
import { dockFadeBehind, dockGlassDark } from "@/shared/theme/dockGlass";
import { isRTLText } from "@/shared/utils/formatters";
import { toast } from "@/shared/hooks/useToast";
import { ApiClientError } from "@/shared/api/client";
import { useNotesBasePath } from "./useNotesBasePath";
import { useCreateTypedNote, useNote, useUpdateNote } from "./api";
import { notePointLevels, notePoints } from "./schemas";
import { filledRowLevels, pointLabels } from "./pointLevels";
import { capitalizeFirst } from "./capitalizeFirst";
import { offerNewAgenda, type AgendaPickerState } from "./agendaHandoff";

/** A row carries an id so React never keys it by position. `level`: 0 = a
 *  point, 1 = a sub-point of the point above (Sub-points, DES 12-10-2026). */
type ItemRow = { id: string; text: string; level: number };

let rowSeq = 0;
const makeRow = (text = "", level = 0): ItemRow => {
  rowSeq += 1;
  return { id: `row_${rowSeq}`, text, level };
};

/** Grow the box to its content — a point is a sentence, not a label. */
function autoSize(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

export function NoteComposePage() {
  const { noteId } = useParams<{ noteId: string }>();
  const isEditing = Boolean(noteId);
  const navigate = useNavigate();
  const location = useLocation();
  // Which shell this page is mounted in. Notes live in both, and the base only
  // decides where Cancel and a successful save land.
  const base = useNotesBasePath() ?? "/app";

  // Agendas (DES 12-10-2026): written from the Agendas tab or a meeting's
  // picker; an existing note keeps its own kind.
  const [searchParams] = useSearchParams();
  const newKind = searchParams.get("kind") === "agenda" ? "agenda" : "note";
  const fromPicker = (location.state as AgendaPickerState | null)?.agendaPicker === true;

  const existing = useNote(noteId ?? null, isEditing);
  const create = useCreateTypedNote();
  const update = useUpdateNote();

  const [title, setTitle] = useState("");
  const [items, setItems] = useState<ItemRow[]>(() => [makeRow()]);
  const [hydrated, setHydrated] = useState(!isEditing);
  const [dirty, setDirty] = useState(false);

  const inputRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  /** Row to focus once it has mounted (set when adding or removing a line). */
  const pendingFocusRef = useRef<string | null>(null);

  const note = existing.data;
  // A recording's POINTS are editable now (ADR-025) — they are the model's
  // reading, and correcting one is the point. Its transcript is not, being the
  // record of what was said, and this editor never touches it.
  const isRecording = isEditing && note?.source !== "typed";
  const isAgenda = isEditing ? note?.kind === "agenda" : newKind === "agenda";

  // Load once. Re-running on every refetch would overwrite what the user has
  // typed since — the server copy is the starting point, not a live source.
  useEffect(() => {
    if (!isEditing || hydrated || !note) {
      return;
    }
    const lines = notePoints(note);
    const levels = notePointLevels(note, lines);
    setTitle(note.title ?? "");
    setItems(lines.length ? lines.map((l, i) => makeRow(l, levels?.[i] ?? 0)) : [makeRow()]);
    setHydrated(true);
  }, [isEditing, hydrated, note]);

  // Focus lands after the render that created the row — inside the state
  // updater the element does not exist yet.
  useLayoutEffect(() => {
    const id = pendingFocusRef.current;
    if (!id) return;
    pendingFocusRef.current = null;
    const el = inputRefs.current[id];
    el?.focus();
    if (el) {
      el.setSelectionRange(el.value.length, el.value.length);
      autoSize(el);
    }
  }, [items]);

  // Re-measure after hydration, or a loaded multi-line point renders one row
  // tall with its own scrollbar.
  useLayoutEffect(() => {
    if (hydrated) {
      Object.values(inputRefs.current).forEach(autoSize);
    }
  }, [hydrated]);

  /** Leaving with unsaved text loses it — the browser asks first. */
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const setItemText = useCallback((id: string, text: string) => {
    setDirty(true);
    // The ONLY mechanism that opens the next point — Return and a pasted block
    // arrive by the same path. Invariant: one row is one line.
    const lines = text.split("\n");
    setItems((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      if (idx < 0) return prev;
      if (lines.length === 1) {
        const next = [...prev];
        // First letter typed into an empty point is a capital (07-10-2026).
        next[idx] = { ...next[idx], text: prev[idx].text === "" ? capitalizeFirst(text) : text };
        return next;
      }
      // Sub-points (DES 12-10-2026): new rows keep the level they were opened
      // from, so Return on a sub-point opens the next sub-point.
      const expanded = lines.map((l, i) =>
        i === 0 ? { ...prev[idx], text: l } : makeRow(l, prev[idx].level),
      );
      // Follow the caret into the last row the split produced, so Return lands
      // the cursor on the new point rather than leaving it behind.
      pendingFocusRef.current = expanded[expanded.length - 1].id;
      return [...prev.slice(0, idx), ...expanded, ...prev.slice(idx + 1)];
    });
  }, []);

  const addItemAtEnd = useCallback(() => {
    const row = makeRow();
    pendingFocusRef.current = row.id;
    setItems((prev) => [...prev, row]);
  }, []);

  const removeItem = useCallback((id: string) => {
    setDirty(true);
    setItems((prev) => {
      // Never leave the editor with nothing to type into.
      if (prev.length === 1) return [makeRow()];
      const idx = prev.findIndex((r) => r.id === id);
      if (idx < 0) return prev;
      const focusTarget = prev[idx - 1] ?? prev[idx + 1];
      if (focusTarget) pendingFocusRef.current = focusTarget.id;
      delete inputRefs.current[id];
      return prev.filter((r) => r.id !== id);
    });
  }, []);

  /** Sub-points (DES 12-10-2026): one level; the first point stays a point. */
  const setItemLevel = useCallback((id: string, level: number) => {
    setDirty(true);
    setItems((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      if (idx < 0) return prev;
      const nextLevel = idx === 0 ? 0 : Math.max(0, Math.min(1, level));
      if (prev[idx].level === nextLevel) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], level: nextLevel };
      return next;
    });
  }, []);

  /**
   * Backspace on an already-empty line removes it — the list-editor rule.
   * Sub-points (DES 12-10-2026): Tab makes the point a sub-point, Shift+Tab a
   * point again (focus stays in the row), and Backspace on an empty sub-point
   * first brings it back to a point, a second one removes it.
   */
  const handleKeyDown = useCallback(
    (id: string, e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Tab") {
        e.preventDefault();
        setItemLevel(id, e.shiftKey ? 0 : 1);
        return;
      }
      if (e.key !== "Backspace") return;
      const row = items.find((r) => r.id === id);
      if (row && row.text.length === 0 && row.level > 0) {
        e.preventDefault();
        setItemLevel(id, 0);
        return;
      }
      if (row && row.text.length === 0 && items.length > 1) {
        e.preventDefault();
        removeItem(id);
      }
    },
    [items, removeItem, setItemLevel],
  );

  const filledItems = useMemo(() => items.map((r) => r.text.trim()).filter(Boolean), [items]);
  /** Levels aligned with `filledItems` (what is saved); the first is 0. */
  const filledLevels = useMemo(() => filledRowLevels(items), [items]);
  /** 1, 2, a, b, 3: what each row shows on the rail. */
  const labels = useMemo(
    () =>
      pointLabels(
        items.map((r) => r.level),
        items.length,
      ),
    [items],
  );
  const saving = create.isPending || update.isPending;
  const canSave = filledItems.length > 0 && !saving && hydrated;

  const handleSave = async () => {
    if (!canSave) return;
    // Blank rows are not points — they are the editor's own scaffolding.
    const text = filledItems.join("\n");
    const trimmedTitle = title.trim();
    try {
      let openId = noteId ?? "";
      if (isEditing && noteId) {
        await update.mutateAsync({
          id: noteId,
          // `points`, not `text`: the server refuses `text` on a recording,
          // and rightly — that field is the transcript's.
          points: filledItems,
          pointLevels: filledLevels,
          ...(trimmedTitle && trimmedTitle !== (note?.title ?? "") ? { title: trimmedTitle } : {}),
        });
      } else {
        const created = await create.mutateAsync({
          text,
          pointLevels: filledLevels,
          ...(trimmedTitle ? { title: trimmedTitle } : {}),
          ...(newKind === "agenda" ? { kind: "agenda" as const } : {}),
        });
        openId = created.id;
        // Written from a meeting's agenda picker: back to the meeting, where
        // it is attached (DES 12-10-2026, the phone's AGENDA_CREATED).
        if (fromPicker) {
          setDirty(false);
          offerNewAgenda({
            id: created.id,
            title: created.title?.trim() || trimmedTitle || "Untitled agenda",
          });
          toast({ title: "Agenda created" });
          // Opened in place (no page to go back to): to the Notes library.
          if (location.key !== "default") navigate(-1);
          else navigate(`${base}/notes`, { replace: true });
          return;
        }
      }
      setDirty(false);
      const noun = isAgenda ? "Agenda" : "Note";
      toast({ title: isEditing ? `${noun} saved` : `${noun} created` });
      // Into the note, as the phone does after saving.
      // Carries where the note was opened from (a meeting), so its back
      // link still returns there (DES 08-10-2026).
      navigate(openId ? `${base}/notes/${openId}` : `${base}/notes`, {
        replace: true,
        state: location.state,
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: e instanceof ApiClientError ? e.message : "Please try again.",
      });
    }
  };

  const heading = isEditing
    ? isRecording
      ? "Edit Points"
      : isAgenda
        ? "Edit Agenda"
        : "Edit Note"
    : isAgenda
      ? "Write an Agenda"
      : "Write a Note";
  const backTo = isEditing && noteId ? `${base}/notes/${noteId}` : `${base}/notes`;

  return (
    <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8">
      {fromPicker && !isEditing ? (
        // From a meeting's Setup: back to the meeting, as it was left.
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to meeting
        </button>
      ) : (
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />{" "}
          {isEditing ? (isAgenda ? "Back to agenda" : "Back to note") : "Notes Library"}
        </Link>
      )}
      <h1 className="mt-3 text-[28px] font-bold leading-9 tracking-tight">{heading}</h1>
      <p className="mt-1 text-[15px] text-muted-foreground">
        {existing.isLoading && isEditing ? (
          <Skeleton className="inline-block h-4 w-24 align-middle" />
        ) : (
          `${filledItems.length} point${filledItems.length === 1 ? "" : "s"} · free · Tab for a sub-point`
        )}
      </p>

      {/*
        The title is the page's first line, not a labelled field: borderless and
        large, as on the phone.
      */}
      <input
        value={title}
        onChange={(e) => {
          setTitle(title === "" ? capitalizeFirst(e.target.value) : e.target.value);
          setDirty(true);
        }}
        // First letter a capital when left (DES 12-10-2026).
        autoCapitalize="sentences"
        onBlur={() => setTitle((t) => capitalizeFirst(t))}
        placeholder="Title"
        maxLength={120}
        dir={isRTLText(title) ? "rtl" : "ltr"}
        className="mt-8 w-full border-0 bg-transparent p-0 text-[26px] font-bold leading-[34px] text-foreground outline-none placeholder:text-muted-foreground/50 focus:ring-0"
      />

      <ol className="mt-5">
        {items.map((row, index) => {
          // Direction is decided per LINE: an agenda often mixes an Urdu point
          // with an English one.
          const rtl = isRTLText(row.text);
          const last = index === items.length - 1;
          // Sub-points (DES 12-10-2026): the rail runs straight past a
          // sub-point unbroken, and its letter sits indented past the rail.
          const sub = index > 0 && row.level > 0;
          return (
            <li key={row.id} className="group flex gap-3">
              {sub ? (
                <div className="flex w-8 shrink-0 flex-col items-center" aria-hidden>
                  <span className="h-5 w-0.5 bg-border" />
                  <span className={cn("w-0.5 flex-1", last ? "bg-transparent" : "bg-border")} />
                </div>
              ) : (
                <div className="flex w-8 shrink-0 flex-col items-center" aria-hidden>
                  <span
                    className={cn("h-1.5 w-0.5", index === 0 ? "bg-transparent" : "bg-border")}
                  />
                  <span className="text-lg font-semibold tabular-nums leading-7 text-muted-foreground">
                    {labels[index]}
                  </span>
                  <span className={cn("w-0.5 flex-1", last ? "bg-transparent" : "bg-border")} />
                </div>
              )}
              {sub ? (
                <span
                  aria-hidden
                  className="ml-2 w-5 shrink-0 py-1.5 text-base font-semibold leading-6 text-muted-foreground"
                >
                  {labels[index]}.
                </span>
              ) : null}
              {/* The field is as wide as its text (field-sizing), so the
                  indent button sits just after the words, not at the far
                  edge (DES 12-10-2026). Clicking the empty space to the
                  right still puts the cursor in the point. */}
              <div
                className="flex min-w-0 flex-1 cursor-text items-start gap-2"
                onMouseDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  e.preventDefault();
                  const el = inputRefs.current[row.id];
                  el?.focus();
                  el?.setSelectionRange(el.value.length, el.value.length);
                }}
              >
                <textarea
                  ref={(el) => {
                    inputRefs.current[row.id] = el;
                  }}
                  value={row.text}
                  rows={1}
                  dir={rtl ? "rtl" : "ltr"}
                  onChange={(e) => {
                    autoSize(e.currentTarget);
                    setItemText(row.id, e.target.value);
                  }}
                  onKeyDown={(e) => handleKeyDown(row.id, e)}
                  placeholder={index === 0 ? "First point…" : sub ? "Sub-point…" : "Next point…"}
                  className="mb-3 min-h-10 w-full min-w-0 resize-none overflow-hidden border-0 bg-transparent px-0 py-1.5 text-base leading-6 text-foreground outline-none placeholder:text-muted-foreground/50 focus:ring-0 supports-[field-sizing:content]:w-auto supports-[field-sizing:content]:max-w-full supports-[field-sizing:content]:[field-sizing:content]"
                />
                {/* Visible indent control (DES 12-10-2026: Tab alone could not
                  be found). Shown on the row being hovered or typed in; the
                  first point has none, it is always a point. */}
                {index > 0 ? (
                  <button
                    type="button"
                    // Keep focus in the textarea, so typing carries on.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setItemLevel(row.id, sub ? 0 : 1)}
                    title={sub ? "Make it a point (Shift+Tab)" : "Make it a sub-point (Tab)"}
                    aria-label={sub ? "Make it a point" : "Make it a sub-point"}
                    className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100"
                  >
                    {sub ? (
                      <ListIndentDecrease className="h-4 w-4" />
                    ) : (
                      <ListIndentIncrease className="h-4 w-4" />
                    )}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {/*
        Clicking the space under the last point appends one, the way a paper
        list works. Return already opens the next point.
      */}
      <button
        type="button"
        onClick={addItemAtEnd}
        aria-label="Add a point"
        className="min-h-[120px] w-full cursor-text"
      />

      {/* THE dock fade behind it; in dark the Save pill is THE dock glass
          with a primary-text label (DES 08-10-2026). */}
      <div
        className={cn(
          "pointer-events-none sticky bottom-6 z-20 mt-auto flex justify-center pt-6",
          dockFadeBehind,
        )}
      >
        <Button
          onClick={() => void handleSave()}
          disabled={!canSave}
          className={cn(
            "pointer-events-auto h-14 w-full max-w-md rounded-full text-base font-bold shadow-[0_8px_30px_rgba(0,0,0,0.12)]",
            dockGlassDark,
            "dark:text-app-text",
          )}
        >
          {saving
            ? "Saving…"
            : isEditing
              ? "Save changes"
              : isAgenda
                ? "Save agenda"
                : "Save note"}
        </Button>
      </div>
    </div>
  );
}

export default NoteComposePage;
